import { useState } from "react";
import FadeIn from "@/components/FadeIn";
import Lightbox from "@/components/Lightbox";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES } from "@/lib/images";

// A curated walk through the property — atmospheric, then buildings, interiors, gardens, close-ups.
// Sequenced so scrolling feels like actually walking the land.
const GALLERY = [
  // Opening: sky + sweeping property
  IMAGES.imgSkylineClouds,
  IMAGES.imgFarmOverviewAerial,
  IMAGES.imgSkylineSunsetField,
  IMAGES.imgWalkTheLand2,
  IMAGES.imgOverviewPondBench,
  IMAGES.imgOverviewSnowyPond,
  IMAGES.imgOverviewSnowyBarn,
  IMAGES.imgOverviewSnowyMountain,
  IMAGES.imgSunflowersMountain,
  IMAGES.imgSunflowersDusk,
  IMAGES.imgOatsDuskField,
  IMAGES.imgVegGardenPergolaHD,
  IMAGES.imgHilltopSunsetBenches,
  IMAGES.imgHilltop6,
  IMAGES.imgHilltopWindowWinter,
  IMAGES.imgHilltopSnowyWarm,
  IMAGES.imgHilltopPorchSummer,
  IMAGES.hilltopSunset,
  IMAGES.oct26HilltopSunset,
  IMAGES.oct26HilltopFirepit,
  IMAGES.oct26FarmSkyline,
  IMAGES.oct26FarmHillside,
  IMAGES.oct26BunnySkyline,
  IMAGES.oct26FarmFlag,
  IMAGES.oct26FarmLeaves,
  IMAGES.imgOverviewCattail3,
  IMAGES.imgBarnPondCattails,
  IMAGES.imgBarnDeckChairs,
  IMAGES.imgBarnEchinacea,
  IMAGES.imgBarnWinterSide,
  // Wildlife moments
  IMAGES.imgHeronWillow,
  IMAGES.imgChickens,
  IMAGES.oct26FarmDeer,
  IMAGES.oct26SwallowtailZinnia,
  IMAGES.oct26BeeOnZinnia,
  IMAGES.oct26Anemones,
  IMAGES.oct26GardenColeus,
  // Cottages + buildings
  IMAGES.imgCattailFront,
  IMAGES.imgCattailOutsideUser,
  IMAGES.imgCreeksideCoverNew,
  IMAGES.imgCreeksideShedTable,
  IMAGES.imgCreeksideBoardwalk,
  IMAGES.imgCreeksideCreek,
  IMAGES.imgLoftExterior,
  IMAGES.imgGardenBridge,
  IMAGES.imgGardenCottageBack,
  IMAGES.imgReflectionCottage7,
  IMAGES.imgReflectionCottage6,
  // Interiors — a slower rhythm inside
  IMAGES.imgBarnKitchenHD,
  IMAGES.imgWellnessStudioLounge,
  IMAGES.imgGymLogo,
  IMAGES.imgCornCribSaunaHD,
  IMAGES.imgCattailIntUser1,
  // Gardens + close-ups
  IMAGES.imgVegGardenHenDenHD,
  IMAGES.imgVegGardenEdit,
  IMAGES.imgFlowerRed,
  IMAGES.newGarden04,
  IMAGES.imgTrailNature,
  IMAGES.imgCreeksideShower,
  // MLS + walking-the-land shots for depth
  IMAGES.mlsMistyHills,
  IMAGES.mlsGoldenHourSnow,
  IMAGES.imgPondFeature,
  IMAGES.imgPropertyOverview,
  IMAGES.imgFlowers3,
  IMAGES.driveView04,
];

export default function Gallery() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  return (
    <div data-testid="gallery-page">
      <section className="container-x pt-12 lg:pt-24 pb-8" data-testid="gallery-hero">
        <FadeIn><p className="overline mb-5">Life Here</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            A genuine working farm,
            <br />
            <em className="not-italic text-clay">thoughtfully shared with guests.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            A closer look at the property — the gardens in early spring,
            the pond on a still morning, quiet corners of the trails, and everywhere in between.
            Click any photo to make it larger.
          </p>
        </FadeIn>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" />

      <section className="container-x py-16 lg:py-24" data-testid="gallery-grid">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[180px] gap-4 lg:gap-6">
          {GALLERY.map((src, i) => {
            // Create a varied grid — some tall, some wide, most single-cell
            const span =
              i % 11 === 3 ? "row-span-2" :
              i % 13 === 5 ? "col-span-2" :
              i % 17 === 7 ? "col-span-2 row-span-2" : "";
            // Rotating organic shapes — every variant is unmistakably non-square
            const shapes = [
              "rounded-t-[160px] rounded-b-[40px]",                                             // cathedral arch
              "rounded-[65%_35%_60%_40%_/_55%_45%_55%_50%]",                                    // leaf petal
              "rounded-tl-[110px] rounded-br-[110px] rounded-tr-[60px] rounded-bl-[60px]",      // asymmetric leaf
              "rounded-l-[140px] rounded-r-[40px]",                                             // left full arch
              "rounded-[40%_60%_45%_55%_/_60%_40%_55%_45%]",                                    // organic squircle
              "rounded-t-[140px] rounded-b-[60px]",                                             // domed arbor
              "rounded-tr-[130px] rounded-bl-[130px] rounded-tl-[60px] rounded-br-[60px]",      // pond ripple
              "rounded-[30%_70%_70%_30%_/_55%_45%_55%_45%]",                                    // eye shape
              "rounded-r-[140px] rounded-l-[40px]",                                             // right arch
              "rounded-full",                                                                    // full circle for wide/tall spans
            ];
            const shape = shapes[i % shapes.length];
            return (
              <button
                key={i}
                type="button"
                onClick={() => { setIdx(i); setOpen(true); }}
                className={`${span} ${shape} overflow-hidden bg-moss/10 group cursor-zoom-in shadow-sm hover:shadow-md transition-shadow`}
                data-testid={`gallery-tile-${i}`}
              >
                <img src={src} alt="Iron Talon Farm" loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              </button>
            );
          })}
        </div>
      </section>

      <Lightbox
        open={open}
        images={GALLERY}
        index={idx}
        title="Walk the Land"
        onClose={() => setOpen(false)}
        onIndexChange={setIdx}
      />
    </div>
  );
}
